import React from "react";
import { SquarePen } from "lucide-react";

/**
 * The conversation history sidebar. Presentational: it renders rows and reports
 * clicks, and knows nothing about redux or fetching.
 *
 * This is NOT the app's navigation sidebar (src/components/sidebar), which is
 * untouched. Chat history is content, not navigation.
 */
export const ConversationList = ({
    conversations = [],
    activeId = null,
    hasMore = false,
    requesting = false,
    error = null,
    onOpen,
    onNewChat,
    onLoadOlder = () => {},
}) => (
    <div className="flex flex-col h-full w-[260px] shrink-0 border-r border-slate-200 pr-2">
        <button
            type="button"
            data-testid="eli-page-new-chat"
            onClick={onNewChat}
            className="flex items-center gap-2 m-2 px-3 h-10 rounded-md text-sm font-medium hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
            <SquarePen className="w-4 h-4" />
            New chat
        </button>

        {/* Rows win whenever there are any, so a failed or in-flight REFRESH never
            blanks a list the user is already reading. With no rows the three states
            are distinct: a 500 used to render "No past conversations yet", and so did
            every cold load before the fetch resolved, which reads as "you have never
            spoken to Eli" in both cases. */}
        <div className="flex-1 overflow-auto">
            {conversations.length === 0 && error ? (
                <p role="alert" className="px-3 py-2 text-sm text-rose-600">Could not load your conversations</p>
            ) : conversations.length === 0 && requesting ? (
                <p className="px-3 py-2 text-sm text-slate-500">Loading your conversations...</p>
            ) : conversations.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-500">No past conversations yet</p>
            ) : (
                <ul>
                    {conversations.map((c) => (
                        <li key={c.conversation_id}>
                            <button
                                type="button"
                                onClick={() => onOpen(c.conversation_id)}
                                aria-current={c.conversation_id === activeId ? "true" : undefined}
                                className={`w-full text-left px-3 py-2 text-sm rounded-md truncate hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                                    c.conversation_id === activeId ? "bg-slate-100 font-medium" : ""
                                }`}
                                title={c.title ?? "Untitled conversation"}
                            >
                                {c.title ?? "Untitled conversation"}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>

        {/* Shown only when the last page came back full. Without it a user past 50
            conversations could never reach the older ones, and a silent cap reads
            as data loss. */}
        {hasMore && (
            <button
                type="button"
                data-testid="eli-load-older"
                onClick={onLoadOlder}
                className="m-2 px-3 h-9 rounded-md text-sm hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
                Load older
            </button>
        )}
    </div>
);

export default ConversationList;
